import { useState, useEffect, useCallback } from 'react';
import { 
  CashRegisterState, 
  CashCount, 
  FinalBalance, 
  CreateExpenseData,
  DailyExpense,
  OpenCashRegisterData
} from '../types/cash-register.types';
import { cashRegisterService } from '../services/cashRegisterService';

const INITIAL_CASH_COUNT: CashCount = {
  bills: {},
  coins: {},
  totalCounted: 0,
};

const INITIAL_STATE: CashRegisterState = {
  dailySummary: null,
  expenses: [],
  cashCount: INITIAL_CASH_COUNT,
  finalBalance: null,
  isExpenseModalOpen: false,
  isIncomeModalOpen: false,
  loading: true,
  isOpened: false,
  openingAmount: 0,
  currentShift: undefined,
  actualShift: undefined,
  shiftSchedule: undefined,
};

export const useCashRegister = (locationId: string, date: string) => {
  const [state, setState] = useState<CashRegisterState>(INITIAL_STATE);

  // Load shift summary and check if cash register is opened for current shift
  useEffect(() => {
    const loadShiftSummary = async () => {
      try {
        setState(prev => ({ ...prev, loading: true }));
        
        // Get current shift status
        const shiftStatus = await cashRegisterService.getCashRegisterStatus(locationId, date);
        
        if (shiftStatus.isOpened) {
          // Get data for the currently open session only, not the entire day
          console.log('🚀 Hook: Trying to get current session summary');
          let summary = await cashRegisterService.getCurrentSessionSummary(locationId, date);
          console.log('📊 Hook: Current session summary result:', summary);
          
          // TEMP: Force use of current session data (no fallback)
          if (!summary) {
            console.log('❌ Hook: No session summary found - using empty data instead of fallback');
            summary = {
              totalSales: 0,
              salesCount: 0,
              cashSales: 0,
              cardSales: 0,
              yapePlinSales: 0,
              appSales: 0,
              openingAmount: 1, // Should match the current session opening amount
              expenses: [],
              date,
              shift: 'MAÑANA',
              currentShift: 'MAÑANA',
              locationId,
              cashRegistersCount: 1
            };
          } else {
            console.log('✅ Hook: Using current session summary');
          }
          
          // Load existing cash count if available
          const existingCashCount = await cashRegisterService.getCashCount(locationId, date);
          
          setState(prev => ({
            ...prev,
            dailySummary: summary,
            expenses: summary.expenses || [],
            cashCount: existingCashCount || INITIAL_CASH_COUNT,
            isOpened: true,
            currentShift: shiftStatus.currentShift,
            actualShift: shiftStatus.actualShift,
            shiftSchedule: shiftStatus.shiftSchedule,
            loading: false
          }));
        } else {
          setState(prev => ({
            ...prev,
            isOpened: false,
            currentShift: shiftStatus.currentShift,
            actualShift: shiftStatus.actualShift,
            shiftSchedule: shiftStatus.shiftSchedule,
            loading: false
          }));
        }
      } catch (error) {
        console.error('Error loading shift summary:', error);
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    loadShiftSummary();
  }, [locationId, date]);

  // Calculate final balance whenever cash count or daily summary changes
  useEffect(() => {
    if (state.dailySummary) {
      // Calcular totales de movimientos (ingresos y egresos)
      const movementsTotal = state.expenses.reduce((acc, movement) => {
        if (movement.type === 'income') {
          return acc + movement.amount;
        } else {
          return acc - movement.amount;
        }
      }, 0);

      // Expected cash = Saldo inicial + Ventas en efectivo + Ingresos - Egresos
      const openingAmount = state.dailySummary.openingAmount || 0;
      const cashSales = state.dailySummary.cashSales || 0;
      const expectedCash = openingAmount + cashSales + movementsTotal;

      const countedCash = state.cashCount.totalCounted || 0;
      const difference = countedCash - expectedCash;
      const canClose = Math.abs(difference) <= 5.0; // Allow up to S/5 difference

      setState(prev => {
        const finalBalance: FinalBalance = {
          expectedCash,
          countedCash,
          difference,
          canClose,
        };
        return { ...prev, finalBalance };
      });
    }
  }, [state.dailySummary, state.cashCount.totalCounted, state.expenses]);

  const updateCashCount = useCallback((cashCount: CashCount) => {
    setState(prev => ({ ...prev, cashCount }));
  }, []);

  const openExpenseModal = useCallback(() => {
    setState(prev => ({ ...prev, isExpenseModalOpen: true }));
  }, []);

  const closeExpenseModal = useCallback(() => {
    setState(prev => ({ ...prev, isExpenseModalOpen: false }));
  }, []);

  const openIncomeModal = useCallback(() => {
    setState(prev => ({ ...prev, isIncomeModalOpen: true }));
  }, []);

  const closeIncomeModal = useCallback(() => {
    setState(prev => ({ ...prev, isIncomeModalOpen: false }));
  }, []);

  const addExpense = useCallback(async (expenseData: CreateExpenseData) => {
    try {
      const newExpense = await cashRegisterService.createExpense(expenseData);
      setState(prev => ({ 
        ...prev, 
        expenses: [...prev.expenses, newExpense],
        isExpenseModalOpen: false,
        isIncomeModalOpen: false 
      }));
    } catch (error) {
      console.error('Error adding expense:', error);
      throw error;
    }
  }, []);

  const deleteExpense = useCallback(async (expenseId: string) => {
    try {
      await cashRegisterService.deleteExpense(expenseId);
      setState(prev => ({ 
        ...prev, 
        expenses: prev.expenses.filter(exp => exp.id !== expenseId)
      }));
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  }, []);


  const openCashRegister = useCallback(async (data: OpenCashRegisterData) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      await cashRegisterService.openCashRegister(data);
      
      // After opening, load the current session summary (not historical data)
      const summary = await cashRegisterService.getCurrentSessionSummary(locationId, date);
      
      setState(prev => ({ 
        ...prev, 
        isOpened: true,
        openingAmount: data.openingAmount,
        dailySummary: summary,
        expenses: summary.expenses || [],
        loading: false 
      }));
    } catch (error: any) {
      setState(prev => ({ ...prev, loading: false }));
      console.error('Error opening cash register:', error);
      
      // If the error is that the cash register is already opened, 
      // reload the data to show the opened state
      if (error.message && error.message.includes('ya está abierta')) {
        try {
          const shiftStatus = await cashRegisterService.getCashRegisterStatus(locationId, date);
          const summary = await cashRegisterService.getCurrentSessionSummary(locationId, date);
          const existingCashCount = await cashRegisterService.getCashCount(locationId, date);
          
          setState(prev => ({ 
            ...prev, 
            dailySummary: summary,
            expenses: summary.expenses || [],
            cashCount: existingCashCount || INITIAL_CASH_COUNT,
            isOpened: true,
            currentShift: shiftStatus.currentShift,
            shiftSchedule: shiftStatus.shiftSchedule,
            loading: false 
          }));
          
          return; // Don't throw the error, just transition to opened state
        } catch (reloadError) {
          console.error('Error reloading cash register data:', reloadError);
        }
      }
      
      throw error;
    }
  }, [locationId, date]);

  const saveCashCount = useCallback(async (cashCount: CashCount, countedBy?: string) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      let savedCashCount: CashCount;
      
      if (state.cashCount.isSaved) {
        // Update existing cash count
        savedCashCount = await cashRegisterService.updateCashCount(locationId, date, cashCount, countedBy);
      } else {
        // Create new cash count
        savedCashCount = await cashRegisterService.saveCashCount(locationId, date, cashCount, countedBy);
      }
      
      setState(prev => ({ 
        ...prev, 
        cashCount: savedCashCount,
        loading: false 
      }));
      
      return savedCashCount;
    } catch (error) {
      setState(prev => ({ ...prev, loading: false }));
      console.error('Error saving cash count:', error);
      throw error;
    }
  }, [locationId, date, state.cashCount.isSaved]);

  const closeCashRegister = useCallback(async () => {
    if (!state.finalBalance?.canClose) {
      throw new Error('Cannot close cash register: difference too high');
    }

    if (!state.finalBalance || !state.dailySummary) {
      throw new Error('Cannot close cash register: missing data');
    }

    try {
      setState(prev => ({ ...prev, loading: true }));
      
      // Debug: Log the cashCount being sent
      console.log('🔍 Closing cash register with cashCount:', state.cashCount);
      console.log('🔍 Total counted being sent:', state.cashCount.totalCounted);
      
      await cashRegisterService.closeCashRegister({
        locationId,
        date,
        cashCount: state.cashCount,
        finalBalance: state.finalBalance,
      });

      // Resetear estado para mostrar la pantalla de apertura
      setState(prev => ({ 
        ...prev, 
        loading: false,
        isOpened: false,
        dailySummary: null,
        expenses: [],
        cashCount: INITIAL_CASH_COUNT,
        finalBalance: null,
        openingAmount: 0
      }));
    } catch (error) {
      setState(prev => ({ ...prev, loading: false }));
      console.error('Error closing cash register:', error);
      throw error;
    }
  }, [state.finalBalance, state.dailySummary, state.cashCount, locationId, date]);

  return {
    state,
    updateCashCount,
    saveCashCount,
    openExpenseModal,
    closeExpenseModal,
    openIncomeModal,
    closeIncomeModal,
    addExpense,
    deleteExpense,
    openCashRegister,
    closeCashRegister,
  };
};